/**
 * NoteBox — a person's notes, at the top of an item (thread) and of a case.
 *
 * Jess learns things by phone ("Called Dave, no answer", "Retains checked,
 * no variance from initial QC") and needs to record a line under his own
 * name, seen by everyone at once and never reworded by Merlin.
 *
 * It sits above layout D's issue line and tiles (Jess's pick), so it takes
 * ONE line until used: the newest note with who and when, "Add a note", and
 * "All N notes" when there are more. "Add a note" opens one text area and a
 * Save button; "All N notes" lists them newest first.
 *
 * Each note is its own append-only record on the server (POST .../notes);
 * nothing here edits or deletes one. A save that fails keeps the typed text
 * in the box (the box stays open) and says why right under it. Notes are
 * plain text: rendered as text (React escapes it), with line breaks kept.
 */
import { useEffect, useRef, useState } from 'react';

import type { DoorNoteRead } from '../../services/qcpInvestigations';
import { formatInvestigationDate } from './investigationsView';
import { NOTE_MAX_CHARS, newestFirst, noteSaveError } from './noteBoxView';
import styles from './NoteBox.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

export interface NoteBoxProps {
  /** The item's or case's id: the box starts over when it changes. */
  recordId: string;
  /** "item" / "case": names what the note is on. */
  noun: 'item' | 'case';
  load: () => Promise<DoorNoteRead[]>;
  save: (text: string) => Promise<DoorNoteRead>;
  /** After a note is saved (the host can refresh its own copy). */
  onSaved?: (note: DoorNoteRead) => void;
}

function NoteRow({ note }: { note: DoorNoteRead }) {
  return (
    <li className={styles.note} data-testid="note">
      <span className={styles.noteMeta} title={new Date(note.created_at).toLocaleString()}>
        {note.author_label} · {formatInvestigationDate(note.created_at)}
      </span>
      {/* The newest note is read whole (UX round 2026-11-03: cut at three
          lines, a status note lost its owners and due dates; the tooltip
          that held the rest was found by nobody). */}
      <p className={styles.noteText}>{note.text}</p>
    </li>
  );
}

export function NoteBox({ recordId, noun, load, save, onSaved }: NoteBoxProps) {
  // A read-only viewer (kanban 808dabf6) reads the notes and writes none.
  const readOnly = useQualityReadOnly();
  const [notes, setNotes] = useState<DoorNoteRead[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [text, setText] = useState('');
  const [writing, setWriting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  // Save and Cancel close the writer, taking the focused button with them;
  // focus goes back to "+ Add a note", the control that opened it. Without
  // this the next Tab landed on the case's stage button (proposal 9).
  const addRef = useRef<HTMLButtonElement | null>(null);
  const refocusAdd = useRef(false);
  useEffect(() => {
    if (writing || !refocusAdd.current) return;
    refocusAdd.current = false;
    addRef.current?.focus();
  }, [writing]);

  useEffect(() => {
    let cancelled = false;
    setNotes([]);
    setLoadFailed(false);
    setText('');
    setWriting(false);
    setError(null);
    setShowAll(false);
    load()
      .then((list) => {
        if (!cancelled) setNotes(newestFirst(list));
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload per record, not per callback identity
  }, [recordId]);

  const trimmed = text.trim();
  const tooLong = trimmed.length > NOTE_MAX_CHARS;

  const onSave = () => {
    if (!trimmed || tooLong || saving) return;
    setSaving(true);
    setError(null);
    save(text)
      .then((note) => {
        setNotes((prev) => newestFirst([note, ...prev.filter((n) => n.id !== note.id)]));
        setText('');
        refocusAdd.current = true;
        setWriting(false);
        onSaved?.(note);
      })
      .catch((err: unknown) => {
        // The typed text stays in the open box: nothing is lost on a failed save.
        setError(noteSaveError(err));
      })
      .finally(() => setSaving(false));
  };

  const onCancel = () => {
    refocusAdd.current = true;
    setWriting(false);
    setText('');
    setError(null);
  };

  const latest = notes[0];
  const inputId = `note-${noun}-${recordId}`;
  return (
    <section className={styles.box} aria-label={`Notes on this ${noun}`} data-testid="note-box">
      <div className={styles.bar}>
        <span className={styles.head}>📝 Notes</span>
        {latest && !showAll ? (
          <ul className={styles.latest} aria-label="Newest note">
            <NoteRow note={latest} />
          </ul>
        ) : (
          !showAll && (
            <span className={styles.hint} data-testid={loadFailed ? 'note-load-error' : undefined}>
              {loadFailed ? 'Earlier notes could not be loaded right now.' : `None on this ${noun} yet.`}
            </span>
          )
        )}
        <span className={styles.barActions}>
          {notes.length > 1 && (
            <button type="button" className={styles.more} onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Fewer' : `All ${notes.length} notes`}
            </button>
          )}
          {!writing && !readOnly && (
            <button type="button" className={styles.more} onClick={() => setWriting(true)} ref={addRef}>
              + Add a note
            </button>
          )}
        </span>
      </div>
      {showAll && (
        <ul className={styles.list} aria-label="Saved notes, newest first">
          {notes.map((n) => (
            <NoteRow key={n.id} note={n} />
          ))}
        </ul>
      )}
      {writing && (
        <div className={styles.writer}>
          <label className={styles.srOnly} htmlFor={inputId}>
            Note
          </label>
          <textarea
            id={inputId}
            className={styles.input}
            rows={2}
            autoFocus
            value={text}
            placeholder={`A line for everyone on this ${noun}, e.g. "Called Dave, no answer"`}
            aria-invalid={error || tooLong ? true : undefined}
            aria-describedby={error ? `${inputId}-error` : undefined}
            onChange={(e) => setText(e.target.value)}
          />
          <div className={styles.actions}>
            <button type="button" className={styles.save} onClick={onSave} disabled={!trimmed || tooLong || saving}>
              {saving ? 'Saving…' : 'Save note'}
            </button>
            <button type="button" className={styles.more} onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <span className={styles.hint}>
              {tooLong
                ? `Too long: ${trimmed.length} of ${NOTE_MAX_CHARS} characters.`
                : 'Saved under your name. Everyone sees it; Merlin never rewrites it.'}
            </span>
          </div>
          {error && (
            <div id={`${inputId}-error`} className={styles.error} role="alert" data-testid="note-error">
              {error}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
